import type { GuidanceAnchor } from '@ziro/legal-shared-types';

/**
 * Which page of the rendered PDF contains this guidance anchor's paragraph?
 *
 * `GuidanceAnchor.text` is the paragraph as it existed in the DOCX; the PDF's
 * text layer is what LibreOffice produced from it. The two rarely match byte for
 * byte — hyphenation, ligatures, table cells and collapsed whitespace all differ
 * — so both sides are normalised to lowercase alphanumerics-and-single-spaces,
 * and only a PREFIX is matched.
 *
 * Returns a 1-based page number, or null when it cannot be found. Null is an
 * expected outcome, not an error: the rail still lists the anchor and its
 * sources, just without a jump.
 */
const PREFIX_WORDS = 8;

function normalise(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

export function locateAnchorPage(anchorText: string, pageTexts: string[]): number | null {
  const needle = normalise(anchorText).split(' ').slice(0, PREFIX_WORDS).join(' ');
  if (!needle || pageTexts.length === 0) return null;
  for (let i = 0; i < pageTexts.length; i++) {
    if (normalise(pageTexts[i] ?? '').includes(needle)) return i + 1;
  }
  return null;
}

/**
 * Batch form of `locateAnchorPage`, for the guidance rail.
 *
 * The rail re-renders on every scroll tick (its parent panel's `page` state
 * is wired to `PdfCanvas`'s `onVisiblePageChange`), so resolving each
 * anchor's page independently — normalising every page's text once PER
 * anchor — turns into anchors × pages full-text regex passes on every
 * scroll. This hoists the per-page normalisation out of the per-anchor
 * loop: each page is normalised exactly once, no matter how many anchors
 * are being located, and the result is meant to be computed inside a
 * `useMemo` keyed on `[anchors, pageTexts]` rather than during render.
 *
 * Returns pages in the same order as `anchors`, agreeing with
 * `locateAnchorPage` anchor-for-anchor.
 */
export function locateAnchorPages(
  anchors: GuidanceAnchor[],
  pageTexts: string[],
): (number | null)[] {
  if (pageTexts.length === 0) return anchors.map(() => null);
  const normalisedPages = pageTexts.map((t) => normalise(t ?? ''));
  return anchors.map((a) => {
    const needle = normalise(a.text).split(' ').slice(0, PREFIX_WORDS).join(' ');
    if (!needle) return null;
    for (let i = 0; i < normalisedPages.length; i++) {
      if (normalisedPages[i].includes(needle)) return i + 1;
    }
    return null;
  });
}
